import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { BrainTapLockup } from "@/components/Logo";
import { Printer, ArrowLeft, QrCode, UserRound, Trophy, Loader2 } from "lucide-react";

const STEPS = [
  { icon: QrCode, title: "Scan", sub: "Point your camera at the code" },
  { icon: UserRound, title: "Avatar", sub: "Build your look in seconds" },
  { icon: Trophy, title: "Play live", sub: "Answer fast, stack streaks, win glory" },
];

export default function Poster() {
  const { code } = useParams();
  const [venue, setVenue] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get(`/venue/public/${code}`)
      .then(({ data }) => setVenue(data.venue))
      .catch(() => setFailed(true));
  }, [code]);

  const joinUrl = `${window.location.origin}/join?code=${code}`;

  return (
    <div className="min-h-screen bg-[#120619] px-4 py-8">
      <style>{`
        .poster-sheet { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        @media print {
          @page { size: letter portrait; margin: 0; }
          body { background: #190a20 !important; }
          .no-print { display: none !important; }
          .poster-sheet { width: 8.5in !important; height: 11in !important; max-width: none !important; border-radius: 0 !important; box-shadow: none !important; margin: 0 !important; }
          .app-shell { padding: 0 !important; }
        }
      `}</style>

      <div className="app-shell mx-auto flex w-full max-w-[8.5in] flex-col items-center">
        <div className="no-print mb-6 flex w-full items-center justify-between">
          <Link to="/venue" className="flex items-center gap-2 rounded-full border border-ice/25 px-5 py-2.5 text-sm font-bold text-mut transition-colors hover:border-amber hover:text-amber" data-testid="poster-back-link">
            <ArrowLeft size={15} /> Back to dashboard
          </Link>
          <button
            data-testid="poster-print-btn"
            onClick={() => window.print()}
            className="flex items-center gap-2 rounded-full bg-amber px-6 py-2.5 font-display text-sm font-extrabold uppercase tracking-wide text-navy-900 transition-transform hover:-translate-y-0.5"
          >
            <Printer size={16} /> Print poster
          </button>
        </div>

        {failed ? (
          <div className="rounded-3xl border border-ice/15 bg-navy-800/70 px-10 py-12 text-center text-mut" data-testid="poster-not-found">
            Room not found — check the join code.
          </div>
        ) : !venue ? (
          <div className="flex h-96 items-center text-mut"><Loader2 className="animate-spin" /></div>
        ) : (
          <div
            data-testid="poster-sheet"
            className="poster-sheet relative flex w-full flex-col overflow-hidden rounded-3xl bg-[#190a20] shadow-[0_30px_90px_rgba(0,0,0,0.6)] sm:aspect-[8.5/11]"
          >
            <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(620px 320px at 50% -6%, rgba(255,77,157,0.28), transparent 62%), radial-gradient(520px 320px at 88% 108%, rgba(196,77,240,0.24), transparent 58%)" }} />

            <header className="relative flex items-center justify-center px-10 pb-5 pt-9">
              <BrainTapLockup className="w-96" />
            </header>

            <main className="relative flex flex-1 flex-col items-center px-12 text-center">
              {venue.logo_url && (
                <img src={venue.logo_url} alt="Venue logo" className="mb-4 max-h-20 rounded-2xl bg-white px-4 py-2 object-contain" data-testid="poster-venue-logo" />
              )}
              <div className="font-mono text-xs font-bold uppercase tracking-[0.45em] text-[#c44df0]">Live bar trivia</div>
              <h1 className="mt-2 font-display text-7xl font-extrabold leading-none text-cream" data-testid="poster-venue-name">{venue.name}</h1>
              <img src="/brand/bt-badge.png" alt="Come for the trivia, leave with a buzz!" className="mt-3 w-80" data-testid="poster-badge" />

              <div className="mt-8 rounded-[2rem] border-2 border-amber/70 bg-white p-6 shadow-[0_0_70px_rgba(245,179,53,0.35)]">
                <QRCodeSVG value={joinUrl} size={264} fgColor="#190a20" data-testid="poster-qr" />
              </div>
              <div className="mt-5 font-display text-2xl font-extrabold uppercase tracking-wide text-cream">Scan to join — play on your phone</div>
              <div className="mt-2 rounded-full border border-ice/25 bg-navy-900/80 px-6 py-2 font-mono text-sm font-bold uppercase tracking-[0.25em] text-amber" data-testid="poster-code-chip">
                No camera? Enter venue code · {venue.code}
              </div>

              <div className="mt-auto flex w-full items-stretch justify-center gap-4 pt-8" data-testid="poster-steps">
                {STEPS.map((s, i) => (
                  <div key={s.title} className="flex w-1/3 flex-col items-center gap-2 rounded-3xl border border-ice/15 bg-navy-900/70 px-4 py-5">
                    <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-[#ff4d9d]">Step {i + 1}</div>
                    <s.icon size={26} className="text-amber" />
                    <div className="font-display text-lg font-extrabold text-cream">{s.title}</div>
                    <div className="text-[11px] leading-snug text-mut">{s.sub}</div>
                  </div>
                ))}
              </div>
            </main>

            <footer className="relative mt-8 bg-amber px-10 py-4 text-center font-display text-sm font-extrabold uppercase tracking-[0.18em] text-navy-900">
              Winners spin the prize wheel · Streaks multiply your points · Earn venue rewards
            </footer>
          </div>
        )}
      </div>
    </div>
  );
}
